import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { SelectPrograma } from "@/components/ui/SelectPrograma";
import { SelectAprendiz } from "@/components/ui/SelectAprendiz";
import { useAprendizById } from "@/hooks/aprendices/useAprendices";
import { useProgramaById } from "@/hooks/programas/useProgramas";
import * as z from "zod";
import {
  useRegisterAprendicesPrograma,
  useUpdateAprendizPrograma,
} from "@/hooks/aprendicesProgramas/useAprendicesProgramas";
import type { AprendizPrograma } from "@/interfaces/aprendizPrograma.interface";

const schema = z.object({
  idAprendiz: z.number().int().positive(),
  idPrograma: z.number().int().positive(),
  progreso: z.number().int().min(0).max(100),
});

type FormValues = z.infer<typeof schema>;

export function RegisterAprendizProgramaForm({
  setOpen,
}: {
  setOpen: (v: boolean) => void;
}) {
  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      idAprendiz: 0,
      idPrograma: 0,
      progreso: 0,
    },
  });

  const { mutate, isPending } = useRegisterAprendicesPrograma();

  const onSubmit = (data: FormValues) => {
    mutate(data, {
      onSuccess: () => {
        setOpen(false);
        form.reset();
      },
    });
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <FormField
          control={form.control}
          name="idAprendiz"
          render={({ field }) => (
            <FormItem>
              <FormLabel required>Aprendiz</FormLabel>
              <FormControl>
                <SelectAprendiz
                  value={
                    field.value
                      ? { value: String(field.value), label: "" }
                      : null
                  }
                  onChange={(option) =>
                    field.onChange(option ? Number(option.value) : null)
                  }
                  placeholder="Selecciona un aprendiz"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="idPrograma"
          render={({ field }) => (
            <FormItem>
              <FormLabel required>Programa</FormLabel>
              <FormControl>
                <SelectPrograma
                  value={
                    field.value
                      ? { value: String(field.value), label: "" }
                      : null
                  }
                  onChange={(option) =>
                    field.onChange(option ? Number(option.value) : null)
                  }
                  placeholder="Selecciona un programa"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        {/* <FormField
          control={form.control}
          name="progreso"
          render={({ field }) => (
            <FormItem>
              <FormLabel>progreso</FormLabel>
              <FormControl>
                <Input type="number" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        /> */}
        <Button type="submit" disabled={isPending} className="w-full">
          {isPending ? "Creando..." : "Registrar Aprendiz en Programa"}
        </Button>
      </form>
    </Form>
  );
}

export function EditAprendizProgramaForm({
  data,
  close,
}: {
  data: AprendizPrograma;
  close: () => void;
}) {
  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      idAprendiz: data.idAprendiz,
      idPrograma: data.idPrograma,
      progreso: data.progreso,
    },
  });

  const { data: aprendizSeleccionado } = useAprendizById(data.idAprendiz);
  const { data: programaSeleccionado } = useProgramaById(data.idPrograma);

  const { mutate, isPending } = useUpdateAprendizPrograma();

  const onSubmit = (values: FormValues) => {
    mutate(
      { ...data, ...values },
      {
        onSuccess: () => close(),
      }
    );
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <FormField
          control={form.control}
          name="idAprendiz"
          render={({ field }) => (
            <FormItem>
              <FormLabel required>Aprendiz</FormLabel>
              <FormControl>
                <SelectAprendiz
                  value={
                    field.value && aprendizSeleccionado
                      ? {
                          value: String(field.value),
                          label:
                            aprendizSeleccionado.usuario.nombre +
                            " " +
                            aprendizSeleccionado.usuario.apellidoPaterno +
                            " " +
                            aprendizSeleccionado.usuario.apellidoMaterno,
                        }
                      : null
                  }
                  onChange={(option) =>
                    field.onChange(option ? Number(option.value) : null)
                  }
                  placeholder="Selecciona un aprendiz"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="idPrograma"
          render={({ field }) => (
            <FormItem>
              <FormLabel required>Programa</FormLabel>
              <FormControl>
                <SelectPrograma
                  value={
                    field.value && programaSeleccionado
                      ? {
                          value: String(field.value),
                          label: programaSeleccionado.nombre,
                        }
                      : null
                  }
                  onChange={(option) =>
                    field.onChange(option ? Number(option.value) : null)
                  }
                  placeholder="Selecciona un programa"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        {/* progreso */}
        <FormField
          control={form.control}
          name="progreso"
          render={({ field }) => (
            <FormItem>
              <FormLabel>progreso</FormLabel>
              <FormControl>
                <Input type="number" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <Button type="submit" disabled={isPending} className="w-full">
          {isPending ? "Guardando..." : "Guardar Cambios"}
        </Button>
      </form>
    </Form>
  );
}

export function DetalleAprendizPrograma({ data }: { data: AprendizPrograma }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
      <div>
        <strong>ID Aprendiz:</strong> {data.idAprendiz}
      </div>
      <div>
        <strong>ID Programa:</strong> {data.idPrograma}
      </div>
      <div>
        <strong>Progreso:</strong> {data.progreso}%
      </div>
      <div>
        <strong>Creado por:</strong> {data.creadoPor}
      </div>
      <div>
        <strong>Actualizado por:</strong> {data.actualizadoPor}
      </div>
      <div>
        <strong>Activo:</strong> {data.isActivo ? "Sí" : "No"}
      </div>
      <div>
        <strong>Oculto:</strong> {data.isOculto ? "Sí" : "No"}
      </div>
      <div>
        <strong>Creado el:</strong> {new Date(data.creadoEl).toLocaleString()}
      </div>
      <div>
        <strong>Actualizado el:</strong>{" "}
        {new Date(data.actualizadoEl).toLocaleString()}
      </div>
    </div>
  );
}
